import { db } from './Database';

export interface StackTestPallet {
    id: number;
    name: string;
    stackTestLayoutWeek: string | null;
    stackPattern: string | null;
    lpnCount: number;
    recommendedMinimumPallets?: number;
}

export class StackTestLayoutService {
    private static readonly STACK_WEEKS = [
        "Stack start(stacked+30m)",
        "Stack Week-1",
        "Stack Week-2",
        "Stack Week-3",
        "Stack Week-4 (unstacking+30M)"
    ];

    // Get pallets for test stack
    static async getPalletsForTestStack(projectId: number): Promise<StackTestPallet[]> {
        try {
            // Get all pallets for the project
            const pallets = await db.pallets
                .where('projectId')
                .equals(projectId)
                .toArray();

            // Get LPN numbers for each pallet
            const palletsWithLpnCount = await Promise.all(pallets.map(async pallet => {
                const lpnNumbers = await db.palletLpnNumbers
                    .where('palletId')
                    .equals(pallet.id!)
                    .toArray();

                // Count LPNs with matching end status
                const lpnCount = lpnNumbers.filter(lpn =>
                    this.STACK_WEEKS.includes(lpn.endStatusPallet)
                ).length;

                // Calculate recommended minimum pallets based on stack pattern
                const recommendedMinimumPallets = this.getRecommendedMinimumPallets(pallet.stackPattern);

                return {
                    id: pallet.id!,
                    name: pallet.name,
                    stackTestLayoutWeek: pallet.stackTestLayoutWeek || null,
                    stackPattern: pallet.stackPattern || null,
                    lpnCount,
                    recommendedMinimumPallets
                };
            }));

            return palletsWithLpnCount;
        } catch (error) {
            console.error('Error getting pallets for test stack:', error);
            throw error;
        }
    }

    // Update pallets for test stack
    static async updatePalletsForTestStack(projectId: number, pallets: StackTestPallet[]): Promise<void> {
        return await db.transaction('rw', [db.pallets], async () => {
            try {
                const timestamp = new Date().toISOString();

                await Promise.all(pallets.map(async pallet => {
                    await db.pallets.update(pallet.id, {
                        stackTestLayoutWeek: pallet.stackTestLayoutWeek,
                        stackPattern: pallet.stackPattern,
                        updatedAt: timestamp
                    });
                }));
            } catch (error) {
                console.error('Error updating pallets for test stack:', error);
                throw error;
            }
        });
    }

    // Helper function to get recommended minimum pallets based on stack pattern
    private static getRecommendedMinimumPallets(pattern: string | null): number {
        switch (pattern) {
            case "2-HIGH COLUMN":
                return 6;
            case "2-HIGH PYRAMID":
                return 6;
            case "3-HIGH COLUMN":
                return 12;
            case "3-HIGH PYRAMID":
                return 10;
            case "4-HIGH COLUMN":
                return 16;
            case "4-HIGH PYRAMID":
                return 14;
            default:
                return 0;
        }
    }
}
